import { useTranslation } from "react-i18next";
import { Clock, Music } from "lucide-react";

import { backgroundTrack } from "../../domain/backgroundTracks";
import { Meditation, formatDuration } from "../../domain/types";
import MeditationPlaybackButton from "./MeditationPlaybackButton";

type Props = {
    meditation: Meditation;
    /** The title's heading level: the page's own, or one inside a dialog. */
    titleAs?: "h1" | "h2";
};

/**
 * A meditation as written: who, how long, on what terms, its recording and
 * its script. Shared by the full page and the preview in the catalogue, so
 * what a therapist reads before adding one is what they read after.
 */
export default function MeditationDetails({ meditation, titleAs: Title = "h1" }: Props) {
    const { t } = useTranslation();
    const Subtitle = Title === "h1" ? "h2" : "h3";
    const track = backgroundTrack(meditation.background_track);
    const tags = [
        ...meditation.therapy_types.map((type) => ({
            key: type,
            label: t(`session.therapyOptions.${type}`),
        })),
        ...(meditation.custom_therapy_types ?? []).map((label) => ({
            key: label,
            label,
        })),
        ...meditation.presenting_problems.map((problem) => ({
            key: problem,
            label: t(`publicDirectory.problems.${problem}`),
        })),
        ...(meditation.custom_presenting_problems ?? []).map((label) => ({
            key: label,
            label,
        })),
    ];

    return (
        <>
            <section className="rounded-2xl border border-border-subtle bg-surface-raised p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                        <Title className="text-xl font-semibold text-content-primary">
                            {meditation.title}
                        </Title>
                        {meditation.author_name && (
                            <p className="mt-1 flex items-center gap-2 text-sm text-content-tertiary">
                                {meditation.author_photo_url && (
                                    <img
                                        src={meditation.author_photo_url}
                                        alt=""
                                        loading="lazy"
                                        className="size-6 shrink-0 rounded-full object-cover"
                                    />
                                )}
                                {t("meditation.byAuthor", {
                                    author: meditation.author_name,
                                })}
                            </p>
                        )}
                    </div>
                    <span className="rounded-full bg-surface-sunken px-2 py-1 text-xs font-medium text-content-secondary">
                        {t(`meditation.kinds.${meditation.kind}`)}
                    </span>
                </div>

                {meditation.description && (
                    <p className="mt-3 text-sm text-content-secondary">
                        {meditation.description}
                    </p>
                )}

                <dl className="mt-4 flex flex-wrap gap-4 text-sm text-content-secondary">
                    <div className="flex items-center gap-1.5">
                        <Clock size={15} aria-hidden="true" />
                        <dt className="sr-only">{t("meditation.duration")}</dt>
                        <dd>
                            {meditation.declared_duration_seconds
                                ? formatDuration(
                                      meditation.declared_duration_seconds
                                  )
                                : formatDuration(meditation.estimated_seconds)}
                        </dd>
                    </div>
                    {meditation.declared_duration_seconds ? (
                        <div className="flex items-center gap-1.5">
                            <dt className="sr-only">
                                {t("meditation.promptedDuration")}
                            </dt>
                            <dd>
                                {t("meditation.fields.byPrompter", {
                                    duration: formatDuration(
                                        meditation.estimated_seconds
                                    ),
                                })}
                            </dd>
                        </div>
                    ) : null}
                    <div className="flex items-center gap-1.5">
                        <Music size={15} aria-hidden="true" />
                        <dt className="sr-only">
                            {t("meditation.backgroundTrack")}
                        </dt>
                        <dd>{t(track.labelKey)}</dd>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <dt className="sr-only">{t("meditation.licenseLabel")}</dt>
                        <dd>{t(`meditation.licenses.${meditation.license}`)}</dd>
                    </div>
                </dl>

                {meditation.has_audio && (
                    <div className="mt-4">
                        <MeditationPlaybackButton
                            meditationId={meditation.id}
                            title={meditation.title}
                            author={meditation.author_name}
                            durationSeconds={meditation.audio_duration_seconds}
                            backgroundTrack={meditation.background_track}
                            voiceVolume={meditation.voice_volume}
                            backgroundVolume={meditation.background_volume}
                        />
                    </div>
                )}

                {tags.length > 0 && (
                    <ul className="mt-4 flex flex-wrap gap-1.5">
                        {tags.map(({ key, label }) => (
                            <li
                                key={key}
                                className="rounded-full bg-accent-tint px-2 py-0.5 text-xs text-accent-on-tint"
                            >
                                {label}
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {meditation.script && (
                <section className="rounded-2xl border border-border-subtle bg-surface-raised p-4">
                    <Subtitle className="mb-2 text-sm font-semibold text-content-primary">
                        {t("meditation.fields.script")}
                    </Subtitle>
                    {/*
                      * The paragraphs are the pauses — the prompter reads
                      * them that way — so they survive here rather than
                      * being collapsed into one block of text.
                      */}
                    <div className="flex flex-col gap-3 whitespace-pre-line text-sm leading-relaxed text-content-secondary">
                        {meditation.script
                            .split(/\n{2,}/)
                            .map((paragraph, index) => (
                                <p key={index}>{paragraph}</p>
                            ))}
                    </div>
                </section>
            )}
        </>
    );
}
